/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * USE AGENT INSTALLER LOGIC
 *
 * Hook customizado para encapsular toda a lógica de instalação de agentes,
 * incluindo integração com backend, gerenciamento de estado e validações
 */

import { useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AgentBaseService } from "@/features/Agents/services/agentBaseService";
import { useAuth } from "@/features/Auth/state/AuthContext";
import { useSuperAdminOrg } from "@/shared/contexts/SuperAdminOrgContext";
import { post, makeApiError, type ApiError } from "@/shared/utils/fetchApi";
import { resolveApiErrorMessage } from "@/shared/utils/apiErrorMessage";
import { logger } from "@/infrastructure/lib/logger";
import {
  AgentWithIntegrations,
  WizardState,
  InstallationResult,
  AgentParameters,
  InstallerField,
} from "@/features/Agents/types/AgentInstaller";
import type { IntegrationBase } from "@/features/Integrations/types/integration";
import { WorkflowBaseService } from "@/features/Workflows/services/WorkflowBaseService";
import { AgentTriggerService } from "../services/agentTriggerService";
import { cleanWorkflowSchema } from "@/features/Workflows/utils/workflow-cleaner";
import { WorkflowPreviewService } from "@/features/Workflows/services/WorkflowPreviewService";
import {
  WorkflowExecutionService,
  WorkflowExecutionPayload,
} from "@/features/Workflows/services/WorkflowExecutionService";
import { useTranslation } from "react-i18next";
import { analyticsService } from "@/shared/services/analytics";

interface ProdgyInstallStreamParams {
  agentId: string;
  workspaceId: string;
  organizationId: string;
  userId: string;
  filters: Array<{ name: string; type: string; value: string }>;
  selectedIntegrations: Array<{
    integration_base_id: string;
    integration_name: string;
    name: string;
  }>;
  parameterValues: Record<string, unknown>;
  promptValues: Array<{ title: string; content: string; block?: boolean }>;
  updateProgress: (stepIndex: number, percent: number, label: string) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}

interface ProdgyInstallStreamResult {
  agentBase: Record<string, unknown>;
  agentVersion: Record<string, unknown>;
  mainTrigger: string;
  linkedIntegrations: Record<string, string>;
  workflowsInstalled: Array<{ id: string; type: string }>;
  installersRun: number;
  installersFailed: number;
}

/**
 * A `type: 'secret'` parameter stores a composite object
 * `{ auth_type, auth_username?, auth_secret?, custom_header_name? }`. The wizard
 * seeds `auth_type`/`auth_username` from the field's `secret_config` BEFORE the
 * user types anything, so a plain "is the value defined / non-empty" check
 * (enough for primitive fields) wrongly passes while the actual credential is
 * still blank — which is how a required webhook secret reached the backend
 * empty. This looks INSIDE the object and requires the real secret for the
 * chosen auth type:
 *   - none            → no credential needed
 *   - basic           → auth_username + auth_secret
 *   - custom          → custom_header_name + auth_secret
 *   - bearer / apikey → auth_secret
 */
function isSecretParameterFilled(value: unknown): boolean {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  const authType = typeof v.auth_type === "string" ? v.auth_type : "basic";
  if (authType === "none") return true;

  const nonEmpty = (k: string): boolean =>
    typeof v[k] === "string" && (v[k] as string).trim() !== "";

  const hasSecret = nonEmpty("auth_secret");
  if (authType === "basic") return nonEmpty("auth_username") && hasSecret;
  if (authType === "custom") return nonEmpty("custom_header_name") && hasSecret;
  return hasSecret; // bearer, apikey, and any future single-secret type
}

/**
 * Consumes the /agent-base/install/stream SSE endpoint for prodgy agents.
 * Maps backend progress events to the wizard's updateProgress callbacks so
 * the prodgy install shows the same granular progress as the legacy flow.
 */
async function runProdgyInstallStream(
  params: ProdgyInstallStreamParams,
): Promise<ProdgyInstallStreamResult> {
  const { updateProgress, t } = params;

  // Pelo `post` com `parseJson: false`: devolve o `Response` cru, que é o que o
  // stream precisa, e ainda assim passa pelo fetchApi — refresh de token no 401 e
  // header `X-Organization-Id`, que o `fetch` cru aqui não tinha.
  let response: Response;
  try {
    response = await post<Response>(
      "/agent-bases/install/stream",
      {
        agentId: params.agentId,
        productId: params.workspaceId,
        organizationId: params.organizationId,
        userId: params.userId,
        filters: params.filters,
        selectedIntegrations: params.selectedIntegrations,
        parameterValues: params.parameterValues,
        promptValues: params.promptValues,
      },
      { parseJson: false },
    );
  } catch (error) {
    const apiError = error as ApiError;
    throw makeApiError(
      `Install stream returned ${apiError?.status ?? "erro"}: ${apiError?.message ?? ""}`,
      {
        status: apiError?.status,
        code: "E_INSTALL_STREAM_FAILED",
      },
    );
  }

  if (!response.body) {
    throw makeApiError("Install stream response has no body", {
      code: "E_INSTALL_STREAM_FAILED",
    });
  }

  let completed: ProdgyInstallStreamResult | null = null;
  let streamError: string | null = null;

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const handleEvent = (eventName: string, dataRaw: string) => {
    let data: Record<string, unknown> = {};
    try {
      data = dataRaw ? JSON.parse(dataRaw) : {};
    } catch {
      return;
    }

    if (eventName === "error") {
      streamError = (data.message as string) || "Unknown install stream error";
      return;
    }

    if (eventName === "complete") {
      completed = {
        agentBase: (data.agent_base as Record<string, unknown>) || {},
        agentVersion: (data.agent_version as Record<string, unknown>) || {},
        mainTrigger: (data.main_trigger as string) || "",
        linkedIntegrations:
          (data.linkedIntegrations as Record<string, string>) || {},
        workflowsInstalled:
          (data.workflowsInstalled as Array<{ id: string; type: string }>) ||
          [],
        installersRun: (data.installersRun as number) || 0,
        installersFailed: (data.installersFailed as number) || 0,
      };
      return;
    }

    if (eventName !== "step") return;

    const step = data.step as string;
    const status = data.status as string;
    if (status !== "done" && step !== "installer_workflow") return;

    switch (step) {
      case "agent_base":
        updateProgress(0, 20, t("installation.installationProcess.copying"));
        break;
      case "workflow_base":
        updateProgress(0, 30, t("installation.installationProcess.copying"));
        break;
      case "integrations":
        updateProgress(
          1,
          40,
          t("installation.installationProcess.integrations"),
        );
        break;
      case "secrets":
        updateProgress(2, 50, t("installation.installationProcess.parameters"));
        break;
      case "parameters":
        updateProgress(2, 60, t("installation.installationProcess.parameters"));
        break;
      case "agent_version":
        updateProgress(3, 80, t("installation.installationProcess.publishing"));
        break;
      case "triggers":
        updateProgress(
          4,
          90,
          t("installation.installationProcess.triggerGenerated"),
        );
        break;
      case "installer_workflow": {
        const index = (data.index as number) || 0;
        const total = (data.total as number) || 0;
        if (total === 0) {
          updateProgress(
            5,
            100,
            t("installation.installationProcess.exporting"),
          );
        } else if (status === "running") {
          const pct = Math.max(0, Math.floor(((index - 1) / total) * 100));
          updateProgress(
            5,
            pct,
            `${t("installation.installationProcess.exporting")} (${index}/${total})`,
          );
        } else {
          updateProgress(
            5,
            100,
            t("installation.installationProcess.exporting"),
          );
        }
        break;
      }
      default:
        break;
    }
  };

  const processBuffer = () => {
    let sepIdx;
    while ((sepIdx = buffer.indexOf("\n\n")) !== -1) {
      const frame = buffer.slice(0, sepIdx);
      buffer = buffer.slice(sepIdx + 2);
      let eventName = "message";
      const dataLines: string[] = [];
      for (const line of frame.split("\n")) {
        if (line.startsWith(":")) continue; // comment / heartbeat
        if (line.startsWith("event:")) {
          eventName = line.slice(6).trim();
        } else if (line.startsWith("data:")) {
          dataLines.push(line.slice(5).trim());
        }
      }
      if (dataLines.length > 0 || eventName !== "message") {
        handleEvent(eventName, dataLines.join("\n"));
      }
    }
  };

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    processBuffer();
    if (streamError || completed) break;
  }

  if (streamError) {
    throw new Error(streamError);
  }
  if (!completed) {
    throw makeApiError("Install stream ended without a complete event", {
      code: "E_INSTALL_STREAM_FAILED",
    });
  }
  return completed;
}

interface UseAgentInstallerLogicProps {
  agent: AgentWithIntegrations;
  onInstallComplete?: (result: InstallationResult) => void;
  onClose?: () => void;
  onRefreshAgentList?: () => Promise<void>;
}

export function useAgentInstallerLogic({
  agent,
  onInstallComplete,
  onClose,
  onRefreshAgentList,
}: UseAgentInstallerLogicProps) {
  const { user } = useAuth();
  const { selectedOrganization } = useSuperAdminOrg();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Agentes podem materializar dashboards próprios (dashboards_config) no
  // install. As queries de dashboard têm staleTime de 5min, então sem invalidar
  // aqui o usuário só vê os novos templates após refresh manual. Invalidação
  // marca as queries (inativas) como stale → refetch ao montar /dashboards.
  const invalidateDashboardQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["available-dashboards"] });
    queryClient.invalidateQueries({ queryKey: ["dashboards"] });
    queryClient.invalidateQueries({ queryKey: ["dashboard-preferences"] });
  }, [queryClient]);

  const [wizardState, setWizardState] = useState<WizardState>({
    currentStep: 0,
    totalSteps: agent.settings?.installer?.steps?.length || 0,
    steps: agent.settings?.installer?.steps || [],
    selectedIntegrations: {},
    parameterValues: {},
    promptValues: [],
    touched: {},
    isInstalling: false,
    installationComplete: false,
    installationProgress: {
      copyingAgent: false,
      linkingIntegrations: false,
      savingParameters: false,
      publishing: false,
      completed: false,
      currentStep: t("installation.installationProcess.currentStep"),
      percentage: 0,
      currentStepIndex: 0,
    },
  });

  // Função que retorna valor auto-preenchido de uma integração (se houver)
  const getAutoFilledValue = useCallback(
    (fieldName: string): string | number | boolean | null => {
      if (!wizardState.selectedIntegrations) return null;

      // Percorrer todas as integrações selecionadas
      for (const integrationList of Object.values(
        wizardState.selectedIntegrations,
      )) {
        if (!Array.isArray(integrationList)) continue;

        for (const integration of integrationList) {
          // Verificar se integration.settings tem o campo compatível
          if (
            integration?.settings &&
            typeof integration.settings === "object"
          ) {
            if (fieldName in integration.settings) {
              let value = integration.settings[fieldName] as
                | string
                | number
                | boolean
                | null
                | undefined;

              // Retornar apenas se o valor não for undefined/null/vazio
              if (value !== undefined && value !== null && value !== "") {
                // Normalizar URLs: remover barra final se existir
                if (
                  typeof value === "string" &&
                  value.match(/^https?:\/\/.+\/$/)
                ) {
                  value = value.replace(/\/$/, "");
                }

                return value;
              }
            }
          }
        }
      }

      return null;
    },
    [wizardState.selectedIntegrations],
  );

  // Verificar se um campo tem valor auto-preenchido de integration_base.settings
  const hasAutoFilledValue = useCallback(
    (fieldName: string): boolean => {
      return getAutoFilledValue(fieldName) !== null;
    },
    [getAutoFilledValue],
  );

  // Validação do step atual
  const validateCurrentStep = useCallback(() => {
    const currentStepData = wizardState.steps[wizardState.currentStep];
    if (!currentStepData) return false;

    switch (currentStepData.name) {
      case "readme":
        return true;

      case "integration":
        if (!currentStepData.fields || !Array.isArray(currentStepData.fields))
          return true;
        return currentStepData.fields.every((field: InstallerField) => {
          if (!field.required) return true;
          const selectedIntegrations =
            wizardState.selectedIntegrations[field.name] || [];
          return selectedIntegrations.length > 0;
        });

      case "parameters": {
        const parameters: AgentParameters = agent.settings?.parameters || {
          fields: [],
        };
        if (!parameters.fields || parameters.fields.length === 0) return true;
        return parameters.fields.every((field: InstallerField) => {
          if (!field.required) return true;

          // Skip validation for hidden fields (showIf condition not met)
          const showIf = (field as unknown as Record<string, unknown>)
            .showIf as string | undefined;
          if (showIf) {
            const depValue = wizardState.parameterValues[showIf];
            if (!depValue || depValue === "false" || depValue === "0")
              return true;
          }

          // Verificar se está preenchido manualmente
          const manualValue = wizardState.parameterValues[field.name];

          // Secret fields hold a composite object that the wizard pre-seeds
          // from secret_config — a generic "is it defined" check passes even
          // with an empty credential. Validate the actual secret inside.
          // `type` is read via cast because InstallerField's union predates the
          // 'secret' parameter type (same pattern used for default/showIf above).
          const fieldType = (field as unknown as Record<string, unknown>).type;
          if (fieldType === "secret") {
            return isSecretParameterFilled(manualValue);
          }

          const hasManualValue =
            manualValue !== undefined &&
            manualValue !== null &&
            manualValue !== "";

          // Verificar se tem valor default definido na field definition
          const hasDefault =
            (field as unknown as Record<string, unknown>).default !==
              undefined &&
            (field as unknown as Record<string, unknown>).default !== null &&
            (field as unknown as Record<string, unknown>).default !== "";

          // Verificar se está auto-preenchido da integração
          const isAutoFilled = hasAutoFilledValue(field.name);

          // Campo válido se tem valor manual, default, OU auto-preenchido
          return hasManualValue || hasDefault || isAutoFilled;
        });
      }

      case "prompts":
        // Step de prompts é sempre válido (não há campos obrigatórios)
        // Usuário pode revisar, alterar ou manter os valores padrão
        return true;

      case "result":
        return true;

      default:
        return true;
    }
  }, [wizardState, agent.settings?.parameters, hasAutoFilledValue]);

  // Navegar para próximo step
  const handleNext = useCallback(() => {
    if (!validateCurrentStep()) {
      setWizardState((prev) => ({
        ...prev,
        touched: {
          ...prev.touched,
          [`step_${prev.currentStep}`]: true,
        },
      }));
      return;
    }

    setWizardState((prev) => ({
      ...prev,
      currentStep: prev.currentStep + 1,
    }));
  }, [validateCurrentStep]);

  // Navegar para step anterior
  const handlePrevious = useCallback(() => {
    setWizardState((prev) => ({
      ...prev,
      currentStep: Math.max(0, prev.currentStep - 1),
    }));
  }, []);

  // Atualizar integrações selecionadas
  const updateSelectedIntegrations = useCallback(
    (fieldName: string, integrations: IntegrationBase[]) => {
      setWizardState((prev) => ({
        ...prev,
        selectedIntegrations: {
          ...prev.selectedIntegrations,
          [fieldName]: integrations,
        },
      }));
    },
    [],
  );

  // Atualizar valores de parâmetros
  const updateParameterValues = useCallback(
    (values: Record<string, string | number | boolean>) => {
      setWizardState((prev) => ({
        ...prev,
        parameterValues: {
          ...prev.parameterValues,
          ...values,
        },
      }));
    },
    [],
  );

  // Atualizar valores de prompts
  const updatePromptValues = useCallback(
    (
      values: Array<{
        id: string;
        title: string;
        content: string;
        block?: boolean;
      }>,
    ) => {
      setWizardState((prev) => ({
        ...prev,
        promptValues: values,
      }));
    },
    [],
  );

  // Marcar campo como tocado
  const markFieldAsTouched = useCallback((fieldName: string) => {
    setWizardState((prev) => ({
      ...prev,
      touched: {
        ...prev.touched,
        [fieldName]: true,
      },
    }));
  }, []);

  // Função auxiliar para atualizar progresso
  const updateProgress = useCallback(
    (stepIndex: number, percentage: number, currentStep: string) => {
      setWizardState((prev) => ({
        ...prev,
        installationProgress: {
          ...prev.installationProgress,
          currentStep,
          percentage,
          currentStepIndex: stepIndex,
        },
      }));
    },
    [],
  );

  // Instalação do agente com integração backend real
  const handleInstall = useCallback(async () => {
    // Usar agent_id ou id como fallback
    const agentId = agent.agent_id || agent.id;
    const installationStartTime = Date.now();

    // Track installation started
    analyticsService.trackEvent("agent_installation_started", {
      agent_name: agent.name,
      agent_type: agent.settings?.basic?.type,
      product_id: agent.product_id || "",
    });

    try {
      setWizardState((prev) => ({ ...prev, isInstalling: true }));

      // 1. Obter ID do usuário do contexto de autenticação
      const userId = user?.id;
      const organizationId =
        selectedOrganization?.id ??
        (user?.user_metadata?.organization_id || user?.organizationId);

      if (!userId) {
        throw makeApiError("User not found in authentication context", {
          code: "E_UNEXPECTED_RESPONSE",
        });
      }

      if (!organizationId) {
        throw makeApiError(
          "Organization ID not found in authentication context",
          { code: "E_UNEXPECTED_RESPONSE" },
        );
      }

      // 2. Montar filtros e selectedIntegrations para envio ao backend
      const filters: Array<{ name: string; type: string; value: string }> = [];
      const selectedIntegrationsPayload: Array<{
        integration_base_id: string;
        integration_name: string;
        name: string;
      }> = [];

      Object.entries(wizardState.selectedIntegrations).forEach(
        ([requirementName, integrations]) => {
          integrations.forEach((integration) => {
            filters.push({
              name: "integration",
              type: "name",
              value: integration.name,
            });
            selectedIntegrationsPayload.push({
              integration_base_id: integration.id,
              integration_name: integration.name,
              name: requirementName,
            });
          });
        },
      );

      // Step 3: Copiar agente para agent_base (backend também vincula integrações e embute IDs nos nodes)
      updateProgress(0, 20, t("installation.installationProcess.copying"));

      if (!agentId) {
        throw makeApiError("Agent ID not found", {
          code: "E_UNEXPECTED_RESPONSE",
        });
      }

      if (!agent.product_id) {
        throw makeApiError("Workspace ID not found", {
          code: "E_UNEXPECTED_RESPONSE",
        });
      }

      // Collect parameter values up-front so the backend can consume them in one call
      // when the agent uses the prodgy executor (new engine). Legacy executor agents
      // still re-collect/resave these after the install call — this is harmless duplication.
      const earlyParameterValues: Record<string, any> = {
        ...wizardState.parameterValues,
      };
      const earlyParameters = agent.settings?.parameters || { fields: [] };
      if (earlyParameters.fields && earlyParameters.fields.length > 0) {
        earlyParameters.fields.forEach((field: any) => {
          if (!(field.name in earlyParameterValues)) {
            const autoFilledValue = getAutoFilledValue(field.name);
            if (autoFilledValue !== null) {
              earlyParameterValues[field.name] = autoFilledValue;
            } else if (field.default !== undefined) {
              earlyParameterValues[field.name] = field.default;
            }
          }
        });
      }
      const earlyPromptValues = wizardState.promptValues.map(
        ({ title, content, block }) => ({
          title,
          content,
          ...(block ? { block: true } : {}),
        }),
      );

      // Prodgy (new engine) executor: use the SSE stream endpoint so the wizard
      // gets real progress events from the server-side atomic install flow.
      // Legacy executors fall through to the multi-call flow below.
      if (agent.executor === "prodgy") {
        const result = await runProdgyInstallStream({
          agentId,
          workspaceId: agent.product_id,
          organizationId,
          userId,
          filters,
          selectedIntegrations: selectedIntegrationsPayload,
          parameterValues: earlyParameterValues,
          promptValues: earlyPromptValues,
          updateProgress,
          t,
        });

        if (onRefreshAgentList) {
          try {
            await onRefreshAgentList();
          } catch {
            // non-fatal
          }
        }

        updateProgress(6, 100, t("installation.installationProcess.finishing"));
        await new Promise((resolve) => setTimeout(resolve, 400));

        const finalResult: InstallationResult = {
          success: true,
          agentBase: {
            data: { data: { agent_base: result.agentBase } },
            error: null,
          },
          agentVersion: result.agentVersion,
          agentBaseId: (result.agentBase?.id as string) || "",
          agentId: (result.agentBase?.id as string) || "",
          trigger: result.mainTrigger,
          message: t("installation.installationProcess.finished"),
        };

        setWizardState((prev) => {
          const resultStepIndex = prev.steps.findIndex(
            (step) => step.name === "result",
          );
          return {
            ...prev,
            isInstalling: false,
            installationComplete: true,
            installationResult: finalResult,
            currentStep:
              resultStepIndex >= 0 ? resultStepIndex : prev.currentStep,
          };
        });

        window.dispatchEvent(new CustomEvent("tour-agent-installed"));

        invalidateDashboardQueries();

        analyticsService.trackEvent("agent_installed", {
          agent_name: agent.name,
          agent_type: agent.settings?.basic?.type,
          product_id: agent.product_id || "",
          installation_duration_ms: Date.now() - installationStartTime,
        });

        return;
      }

      // `POST /agent-bases` deriva organização e usuário do token e copia os
      // settings no servidor; os valores de parâmetro e prompt vão nos passos
      // seguintes deste mesmo fluxo.
      const installResult = await AgentBaseService.installAgent(
        agentId,
        agent.product_id,
        filters,
        selectedIntegrationsPayload,
      );

      if (installResult.error) {
        throw new Error(
          installResult.error.message || "Falha na instalação do agente",
        );
      }

      const installData = installResult.data ?? undefined;
      const agentBase = installData?.agent_base as { id?: string } | undefined;
      const agentBaseId =
        agentBase?.id ||
        (installData?.id as string | undefined) ||
        (installData?.agent_base_id as string | undefined);

      if (!agentBaseId) {
        throw makeApiError("Agent installation failed", {
          code: "E_INSTALL_STREAM_FAILED",
        });
      }

      // Step 4: Integrações já vinculadas pelo backend durante a instalação
      updateProgress(1, 40, t("installation.installationProcess.integrations"));

      // Step 3: Salvar parâmetros preservando settings existentes
      updateProgress(2, 60, t("installation.installationProcess.parameters"));

      // Coletar todos os valores dos parâmetros (manuais + auto-preenchidos + defaults)
      const allParameterValues: Record<string, any> = {
        ...wizardState.parameterValues,
      };

      const parameters = agent.settings?.parameters || { fields: [] };
      if (parameters.fields && parameters.fields.length > 0) {
        parameters.fields.forEach((field: any) => {
          if (!(field.name in allParameterValues)) {
            // Buscar valor auto-preenchido de integrações
            const autoFilledValue = getAutoFilledValue(field.name);
            if (autoFilledValue !== null) {
              allParameterValues[field.name] = autoFilledValue;
            } else if (field.default !== undefined) {
              // Fallback para valor default do campo
              allParameterValues[field.name] = field.default;
            }
          }
        });
      }

      // Persist secret fields to secrets_base (encrypted) and replace values with references
      if (parameters.fields && parameters.fields.length > 0) {
        for (const field of parameters.fields as any[]) {
          if (field.type === "secret" && allParameterValues[field.name]) {
            const secretValue = allParameterValues[field.name];

            // secretValue is an object: { auth_type, auth_username?, auth_secret, custom_header_name? }
            if (typeof secretValue === "object" && secretValue.auth_secret) {
              try {
                // Store each credential field in secrets_base. Mesmo path nos
                // dois backends; v2 ignora organizationId (org vem do JWT).
                const storeSecret = (fieldName: string, value: unknown) => {
                  const body = {
                    source: "agents",
                    sourceId: agentBaseId,
                    fieldName,
                    value,
                    organizationId,
                  };
                  return post("/secrets/store", body);
                };

                await Promise.all([
                  storeSecret(
                    `${field.name}_auth_secret`,
                    secretValue.auth_secret,
                  ),
                  ...(secretValue.auth_username
                    ? [
                        storeSecret(
                          `${field.name}_auth_username`,
                          secretValue.auth_username,
                        ),
                      ]
                    : []),
                ]);

                // Replace value with metadata + references (no plain text secrets)
                allParameterValues[field.name] = {
                  auth_type: secretValue.auth_type || "basic",
                  auth_username: `\${secret_base.${agentBaseId}.${field.name}_auth_username}`,
                  auth_secret: `\${secret_base.${agentBaseId}.${field.name}_auth_secret}`,
                  ...(secretValue.custom_header_name
                    ? { custom_header_name: secretValue.custom_header_name }
                    : {}),
                };
              } catch (secretStoreError) {
                // O valor em claro NÃO fica no payload. `AgentBaseService.update`
                // grava `allParameterValues` em `settings.parameters.values`, que
                // é lido de volta pela tela de configuração e trafega em toda
                // resposta do agente — o "fallback funcional" anterior persistia a
                // credencial em texto puro no banco, justamente o que
                // `/secrets/store` existe para evitar.
                //
                // Some o campo e avisa. Não bloqueia: o agente já está instalado,
                // e recusar aqui levaria a pessoa a instalar de novo; mas sem o
                // aviso a instalação parecia completa e o agente ficava sem
                // credencial, o que reaparece depois como "não autentica" sem
                // pista do motivo.
                delete allParameterValues[field.name];
                logger.error(
                  "[useAgentInstallerLogic] Falha ao armazenar o segredo; o campo foi omitido em vez de gravado em claro",
                  { field: field.name, error: secretStoreError },
                );
                toast.warning(t("installation.warnings.secretNotStored"));
              }
            }
          }
        }
      }

      // Buscar settings atuais uma única vez para salvar parâmetros e prompts
      const currentAgentBase = await AgentBaseService.getById(agentBaseId);
      const currentSettings = currentAgentBase?.settings || {};

      // Preparar updated settings com parâmetros e prompts
      let updatedSettings = { ...currentSettings };

      // Salvar parâmetros se houver
      if (Object.keys(allParameterValues).length > 0) {
        const currentParameters: AgentParameters =
          currentSettings.parameters || {};
        updatedSettings = {
          ...updatedSettings,
          parameters: {
            ...currentParameters,
            values: {
              ...(currentParameters.values || {}),
              ...allParameterValues,
            },
          },
        };
      }

      // Salvar prompts configurados se houver
      if (wizardState.promptValues.length > 0) {
        // Converter para formato Prompt[] (remover id interno, manter block)
        const promptsArray = wizardState.promptValues.map(
          ({ title, content, block }) => ({
            title,
            content,
            updatedAt: new Date().toISOString(),
            ...(block && { block: true }), // Incluir block apenas se for true
          }),
        );

        updatedSettings = {
          ...updatedSettings,
          prompts: promptsArray,
        };
      }

      // Aplicar atualização se houve mudanças
      if (
        Object.keys(allParameterValues).length > 0 ||
        wizardState.promptValues.length > 0
      ) {
        await AgentBaseService.update(agentBaseId, {
          settings: updatedSettings,
        });
      }

      // Step 4: Publicar agente (fluxo diverge entre agentic e engine/internal)
      updateProgress(3, 75, t("installation.installationProcess.publishing"));

      const isAgenticAgent = agent.settings?.basic?.type === "Agentic";
      let publishResponse: any;

      if (isAgenticAgent) {
        // Agentic agents don't use workflow_base — publish directly with settings (prompts, parameters)
        const agenticPublishPayload = {
          name: agent.name,
          product_id: agent.product_id,
          created_by: userId,
          settings: { ...updatedSettings },
          agent_base_id: agentBaseId,
          version: "1.0.0",
          description: agent.description,
        };

        publishResponse = await post<any>(
          `/agents/${agentBaseId}/publish`,
          agenticPublishPayload,
        );

        if (!publishResponse) {
          throw makeApiError("Empty response when publishing agent", {
            code: "E_UNEXPECTED_RESPONSE",
          });
        }
      } else {
        // Engine/internal agents: fetch workflow, clean schema, publish with workflow
        const workflowData = await WorkflowBaseService.getByAgentBaseId(
          agentBaseId,
        ).then((workflows: any[]) =>
          workflows.length > 0 ? workflows[0] : null,
        );

        if (!workflowData) {
          throw makeApiError("Workflow not found for the installed agent", {
            code: "E_UNEXPECTED_RESPONSE",
          });
        }

        const agentBaseData = await AgentBaseService.getById(agentBaseId);

        if (!agentBaseData) {
          throw makeApiError("Agent_base not found after installation", {
            code: "E_UNEXPECTED_RESPONSE",
          });
        }

        updateProgress(3, 80, t("installation.installationProcess.publishing"));

        const cleanedWorkflowSchema = await cleanWorkflowSchema(
          workflowData.settings?.nodes || [],
          workflowData.settings?.edges || [],
          { organizationId: agent.product_id, workspaceId: agent.product_id },
        );

        const settings = {
          ...agentBaseData.settings,
          workflow_id: workflowData.id,
        };

        const publishPayload = {
          name: agent.name,
          product_id: agent.product_id,
          created_by: userId,
          settings: settings,
          agent_base_id: agentBaseId,
          version: "1.0.0",
          description: agent.description,
          workflow_schema: {
            workflow_base_id: workflowData.id,
            edges: cleanedWorkflowSchema.edges || [],
            nodes: cleanedWorkflowSchema.nodes || [],
            prompts: cleanedWorkflowSchema.prompts || [],
            parameters: cleanedWorkflowSchema.parameters || {},
          },
          workflow_settings: workflowData.settings,
        };

        publishResponse = await post<any>(
          `/agents/${agentBaseId}/publish`,
          publishPayload,
        );

        if (!publishResponse) {
          throw makeApiError("Empty response when publishing agent", {
            code: "E_UNEXPECTED_RESPONSE",
          });
        }
      }

      // Step 5: Buscar o trigger da versão publicada
      updateProgress(
        4,
        90,
        t("installation.installationProcess.triggerGenerated"),
      );

      let actualTrigger = `@${agent.name.toLowerCase().replace(/\s+/g, "_")}_v1`; // Fallback

      if (publishResponse.id) {
        try {
          const { data: triggers } = await AgentTriggerService.getByVersionId(
            publishResponse.id,
          );
          if (triggers && triggers.length > 0) {
            if (triggers[0].trigger_config) {
              const config = triggers[0].trigger_config;

              // Usar publicUrl como campo primário, com fallbacks
              const foundTrigger =
                config.publicUrl ||
                config.fullUrl ||
                config.url ||
                actualTrigger;
              actualTrigger = foundTrigger;
            }
          }
        } catch (triggerError) {
          // Mantém o fallback se houver erro
        }
      }

      // Step 5.5: Salvar trigger em settings.parameters.values.main_trigger
      try {
        const currentAgentBase = await AgentBaseService.getById(agentBaseId);
        const currentSettings = currentAgentBase?.settings || {};

        const currentParameters: AgentParameters =
          currentSettings.parameters || {};
        const updatedSettings = {
          ...currentSettings,
          parameters: {
            ...currentParameters,
            values: {
              ...(currentParameters.values || {}),
              main_trigger: actualTrigger,
            },
          },
        };

        await AgentBaseService.update(agentBaseId, {
          settings: updatedSettings,
        });
      } catch (triggerSaveError) {
        // Segue sem bloquear — o agente já está instalado, e recusar aqui levaria
        // a pessoa a instalar de novo. Mas não passa em silêncio: sem aviso, a
        // instalação parecia completa e o agente ficava sem o trigger principal,
        // o que aparece depois como "o agente não roda" sem pista do motivo.
        logger.error(
          "[useAgentInstallerLogic] Falha ao salvar o trigger principal",
          triggerSaveError,
        );
        toast.warning(t("installation.warnings.triggerNotSaved"));
      }

      // Step 6: Exportando - Executar workflows instaladores
      updateProgress(5, 0, t("installation.installationProcess.exporting"));

      try {
        // Buscar workflows tipo "installer" com os mesmos filtros da instalação
        const previewResponse = await WorkflowPreviewService.getPreview(
          agentId,
          "installer",
          filters,
        );

        const installerWorkflows = previewResponse.data?.workflows || [];

        if (installerWorkflows.length > 0) {
          // Buscar prompts e parâmetros do agent_base recém-instalado UMA VEZ
          const installedAgentBase =
            await AgentBaseService.getById(agentBaseId);
          const agentPrompts =
            (installedAgentBase?.settings?.prompts as any) || [];
          // Merge fixed parameters (root-level) with user-entered values
          const parametersObj = (installedAgentBase?.settings?.parameters ||
            {}) as Record<string, unknown>;
          const fixedParams = Object.fromEntries(
            Object.entries(parametersObj).filter(
              ([k]) => k !== "fields" && k !== "values",
            ),
          );
          const agentParameters = {
            ...fixedParams,
            ...((parametersObj.values as Record<string, unknown>) || {}),
          };

          // Executar cada workflow installer sequencialmente
          for (let i = 0; i < installerWorkflows.length; i++) {
            const workflow = installerWorkflows[i];
            const progressPercent = Math.floor(
              (i / installerWorkflows.length) * 100,
            ); // 0% a 100%

            updateProgress(
              5,
              progressPercent,
              `${t("installation.installationProcess.exporting")} (${i + 1}/${installerWorkflows.length})`,
            );

            try {
              // Limpar apenas nodes e edges (sem buscar parâmetros do workflow original)
              const cleanedSchema = await cleanWorkflowSchema(
                workflow.content.nodes || [],
                workflow.content.edges || [],
                {
                  organizationId,
                  workspaceId: agent.product_id,
                },
              );

              // Montar payload com TODOS os parâmetros do agent_base instalado
              const executionPayload: WorkflowExecutionPayload = {
                nodes: cleanedSchema.nodes,
                edges: cleanedSchema.edges,
                prompts: agentPrompts, // Do agent_base instalado
                parameters: agentParameters, // TODOS os parâmetros do agent_base instalado (incluindo main_trigger)
                agentBaseId: agentBaseId,
                organizationId: organizationId,
                workspaceId: agent.product_id,
              };

              // Executar workflow e aguardar conclusão (timeout de 5 minutos)
              const executionResult =
                await WorkflowExecutionService.executeAndWait(
                  executionPayload,
                  300000,
                );

              if (executionResult.status === "error") {
                // Não bloqueia instalação se um installer falhar
                toast.warning(
                  `Instalador ${i + 1} falhou: ${executionResult.error}`,
                );
              }
            } catch (cleanError) {
              // Não bloqueia instalação se falhar ao limpar schema
              toast.warning(`Falha ao preparar instalador ${i + 1}`);
            }
          }
        }
      } catch (installerError) {
        // Não bloqueia a instalação se falhar ao executar instaladores
        toast.warning("Alguns instaladores não puderam ser executados");
      }

      // Step 7: Finalizar e atualizar lista
      updateProgress(6, 0, t("installation.installationProcess.finishing"));

      // Atualizar a lista de agentes durante o "Finalizando" para UX instantânea
      if (onRefreshAgentList) {
        try {
          await onRefreshAgentList();
        } catch (error) {
          // Instalação foi bem-sucedida, mas lista não foi atualizada
        }
      }

      // Finalizar progresso
      updateProgress(6, 100, t("installation.installationProcess.finishing"));

      // Aguardar um pouco para mostrar o progresso completo
      await new Promise((resolve) => setTimeout(resolve, 800));

      const finalResult: InstallationResult = {
        success: true,
        agentBase: installResult,
        agentVersion: publishResponse,
        agentBaseId,
        agentId: agentBaseId,
        trigger: actualTrigger,
        message: t("installation.installationProcess.finished"),
      };

      setWizardState((prev) => {
        const resultStepIndex = prev.steps.findIndex(
          (step) => step.name === "result",
        );
        return {
          ...prev,
          isInstalling: false,
          installationComplete: true,
          installationResult: finalResult,
          currentStep:
            resultStepIndex >= 0 ? resultStepIndex : prev.currentStep,
        };
      });

      // Dispara evento de agente instalado para o tour
      window.dispatchEvent(new CustomEvent("tour-agent-installed"));

      invalidateDashboardQueries();

      // Track successful installation
      analyticsService.trackEvent("agent_installed", {
        agent_name: agent.name,
        agent_type: agent.settings?.basic?.type,
        product_id: agent.product_id || "",
        installation_duration_ms: Date.now() - installationStartTime,
      });
    } catch (error) {
      setWizardState((prev) => ({
        ...prev,
        isInstalling: false,
        installationComplete: false,
      }));

      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      toast.error(resolveApiErrorMessage(error, t));

      // Track installation failure
      analyticsService.trackEvent("agent_installation_failed", {
        agent_name: agent.name,
        agent_type: agent.settings?.basic?.type,
        product_id: agent.product_id || "",
        installation_duration_ms: Date.now() - installationStartTime,
        error_message: errorMessage,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    agent,
    wizardState.selectedIntegrations,
    wizardState.parameterValues,
    wizardState.promptValues,
    onInstallComplete,
    updateProgress,
    getAutoFilledValue,
    user,
    t,
    onRefreshAgentList,
  ]);

  // Cancelar instalação / Fechar wizard
  const handleCancel = useCallback(() => {
    if (wizardState.installationComplete && wizardState.installationResult) {
      onInstallComplete?.(wizardState.installationResult);
    }

    onClose?.();
  }, [
    onClose,
    onInstallComplete,
    wizardState.installationComplete,
    wizardState.installationResult,
  ]);

  return {
    wizardState,
    validateCurrentStep,
    handleNext,
    handlePrevious,
    handleInstall,
    handleCancel,
    updateSelectedIntegrations,
    updateParameterValues,
    updatePromptValues,
    markFieldAsTouched,
  };
}
